<template>
  <div class="terms-page">
    <section class="terms-hero">
      <div class="container">
        <h1>Terms of Service</h1>
        <p>Last updated: June 2023</p>
      </div>
    </section>
    
    <section class="terms-content">
      <div class="container">
        <div class="terms-text">
          <p class="intro">
            Please read these Terms of Service ("Terms", "Terms of Service") carefully before using the Photo Enhancer - AI upscale application 
            (the "Service") operated by us.
          </p>
          
          <p>
            Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. 
            These Terms apply to all visitors, users and others who access or use the Service.
          </p>
          
          <p>
            By accessing or using the Service you agree to be bound by these Terms. If you disagree with any part of the terms 
            then you may not access the Service.
          </p>
          
          <h2>Use of the Service</h2>
          
          <p>
            Photo Enhancer - AI upscale is an application that allows users to enhance and upscale their photos using artificial intelligence technology. 
            The Service is provided on an "as is" and "as available" basis.
          </p>
          
          <p>
            You are responsible for safeguarding your device and ensuring that proper security measures are in place. 
            You agree that you will not engage in any activity that interferes with or disrupts the Service.
          </p>
          
          <h2>User Content</h2>
          
          <p>
            Our Service allows you to enhance and process photos. You retain all rights to your photos. 
            By uploading photos to our Service, you grant us a limited license to process and enhance these photos as part of providing our Service to you.
          </p>
          
          <p>
            You are solely responsible for the photos you process through our Service and must ensure that you have the right to use these photos 
            and that they do not violate any third-party rights or applicable laws.
          </p>
          
          <p>
            We do not claim ownership of your photos, and we do not use your photos for any purpose other than providing the Service to you, 
            except as otherwise described in our Privacy Policy.
          </p>
          
          <h2>Subscriptions and Payments</h2>
          
          <p>
            Some aspects of the Service may be provided for a fee. If you elect to use premium features of the Service, 
            you agree to pay the applicable fees as they become due.
          </p>
          
          <p>
            The Service may offer subscription-based access to premium features ("Subscription"). Subscriptions may be offered at different 
            price points and with different features according to the duration of the Subscription or the type of device you own.
          </p>
          
          <p>
            Subscriptions automatically renew unless auto-renew is turned off at least 24 hours before the end of the current period. 
            Your account will be charged for renewal within 24 hours prior to the end of the current period. You can manage your Subscriptions 
            and turn off auto-renewal by going to your account settings after purchase.
          </p>
          
          <p>
            We reserve the right to change our Subscription fees at any time. If you are a paid user at the time of such change, 
            the new rate will take effect upon your next subscription period.
          </p>
          
          <h3>Free Trials</h3>
          
          <p>
            We may offer free trials for our Subscriptions. When you sign up for a free trial, you acknowledge and agree that unless 
            you actively cancel your Subscription by the end of the free trial, you will be automatically billed for the Subscription 
            at the then-current rate.
          </p>
          
          <h3>Refunds</h3>
          
          <p>
            Refunds are handled according to the refund policies of the app store through which you purchased the Subscription 
            (Google Play Store or Apple App Store). Generally, we do not provide refunds for Subscriptions that have already begun, 
            except as required by applicable law.
          </p>
          
          <p>
            If you believe you are entitled to a refund, please contact us at <a href="mailto:yikenxinxin@gmail.com">yikenxinxin@gmail.com</a> 
            and we will review your request.
          </p>
          
          <h2>Intellectual Property</h2>
          
          <p>
            The Service and its original content, features, and functionality are and will remain the exclusive property of 
            Photo Enhancer and its licensors. The Service is protected by copyright, trademark, and other laws of both the 
            United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product 
            or service without the prior written consent of Photo Enhancer.
          </p>
          
          <h2>Limitation of Liability</h2>
          
          <p>
            In no event shall Photo Enhancer, nor its directors, employees, partners, agents, suppliers, or affiliates, 
            be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, 
            loss of profits, data, use, goodwill, or other intangible losses, resulting from:
          </p>
          
          <ul>
            <li>Your access to or use of or inability to access or use the Service;</li>
            <li>Any conduct or content of any third party on the Service;</li>
            <li>Any content obtained from the Service; and</li>
            <li>Unauthorized access, use or alteration of your transmissions or content,</li>
          </ul>
          
          <p>
            whether based on warranty, contract, tort (including negligence) or any other legal theory, 
            whether or not we have been informed of the possibility of such damage, and even if a remedy 
            set forth herein is found to have failed of its essential purpose.
          </p>
          
          <h2>Disclaimer</h2>
          
          <p>
            Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. 
            The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, 
            implied warranties of merchantability, fitness for a particular purpose, non-infringement or course of performance.
          </p>
          
          <p>
            Photo Enhancer, its subsidiaries, affiliates, and its licensors do not warrant that:
          </p>
          
          <ul>
            <li>The Service will function uninterrupted, secure or available at any particular time or location;</li>
            <li>Any errors or defects will be corrected;</li>
            <li>The Service is free of viruses or other harmful components; or</li>
            <li>The results of using the Service will meet your requirements.</li>
          </ul>
          
          <h2>Governing Law</h2>
          
          <p>
            These Terms shall be governed and construed in accordance with the laws of the United States, 
            without regard to its conflict of law provisions.
          </p>
          
          <p>
            Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. 
            If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of 
            these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Service, 
            and supersede and replace any prior agreements we might have between us regarding the Service.
          </p>
          
          <h2>Changes to Terms</h2>
          
          <p>
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
            If a revision is material we will try to provide at least 30 days notice prior to any new terms taking effect. 
            What constitutes a material change will be determined at our sole discretion.
          </p>
          
          <p>
            By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. 
            If you do not agree to the new terms, please stop using the Service.
          </p>
          
          <h2>Third-Party Services</h2>
          
          <p>
            The Service may contain links to third-party websites or services that are not owned or controlled by Photo Enhancer.
          </p>
          
          <p>
            Photo Enhancer has no control over, and assumes no responsibility for, the content, privacy policies, 
            or practices of any third-party websites or services. You further acknowledge and agree that Photo Enhancer 
            shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by 
            or in connection with use of or reliance on any such content, goods or services available on or through any such websites or services.
          </p>
          
          <p>
            We strongly advise you to read the terms and conditions and privacy policies of any third-party websites or services that you visit.
          </p>
          
          <h2>Termination</h2>
          
          <p>
            We may terminate or suspend access to our Service immediately, without prior notice or liability, 
            for any reason whatsoever, including without limitation if you breach the Terms.
          </p>
          
          <p>
            All provisions of the Terms which by their nature should survive termination shall survive termination, 
            including, without limitation, ownership provisions, warranty disclaimers, indemnity and limitations of liability.
          </p>
          
          <h2>Contact Us</h2>
          
          <p>
            If you have any questions about these Terms, please contact us at 
            <a href="mailto:yikenxinxin@gmail.com">yikenxinxin@gmail.com</a>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'TermsPage',
  metaInfo: {
    title: 'Terms of Service - Photo Enhancer'
  }
}
</script>

<style lang="scss" scoped>
.terms-page {
  padding-bottom: 80px;
}

.terms-hero {
  background: linear-gradient(to right, rgba(26, 26, 46, 0.95), rgba(53, 53, 102, 0.95));
  color: white;
  padding: 120px 0 60px;
  text-align: center;
  
  h1 {
    font-size: 2.8rem;
    margin-bottom: 15px;
  }
  
  p {
    font-size: 1.1rem;
    opacity: 0.8;
  }
}

.terms-content {
  padding: 60px 0;
  
  .terms-text {
    max-width: 800px;
    margin: 0 auto;
    line-height: 1.7;
    
    .intro {
      font-size: 1.1rem;
      margin-bottom: 30px;
      color: var(--text-color);
    }
    
    h2 {
      font-size: 1.8rem;
      margin: 40px 0 20px;
      color: var(--primary-color);
    }
    
    h3 {
      font-size: 1.4rem;
      margin: 30px 0 15px;
      color: var(--text-color);
    }
    
    p {
      margin-bottom: 20px;
      color: var(--text-light);
    }
    
    ul {
      margin: 20px 0 20px 20px;
      
      li {
        margin-bottom: 10px;
        color: var(--text-light);
      }
    }
    
    a {
      color: var(--primary-color);
      text-decoration: none;
      transition: color 0.3s ease;
      
      &:hover {
        color: var(--primary-dark);
        text-decoration: underline;
      }
    }
    
    strong {
      font-weight: 600;
      color: var(--text-color);
    }
  }
}

@media (max-width: 768px) {
  .terms-hero {
    padding: 100px 0 40px;
    
    h1 {
      font-size: 2.2rem;
    }
  }
  
  .terms-content {
    padding: 40px 0;
    
    .terms-text {
      h2 {
        font-size: 1.5rem;
      }
      
      h3 {
        font-size: 1.3rem;
      }
    }
  }
}
</style> 